import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imgSrc } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { ArrowUpRight, Leaf, Coffee, Sparkles } from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_5152b6f5-52b4-445a-b98d-a371357c2724/artifacts/iaif0u89_banner-hero.avif";
const LANDSCAPE_IMG = "/zinc-fence-rest.jpg";
const INTERIOR_IMG = "/rooms.jpg";

export default function Home() {
    const { t } = useT();
    const { format } = useCurrency();
    const [cabins, setCabins] = useState([]);
    const [experiences, setExperiences] = useState([]);
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        api.get("/cabins?featured=true").then((r) => setCabins(r.data)).catch(() => {});
        api.get("/experiences").then((r) => setExperiences(r.data.slice(0, 3))).catch(() => {});
        api.get("/gallery/featured").then((r) => setFeatured(r.data)).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            <Navbar variant="transparent" />

            {/* HERO */}
            <section className="relative h-[90vh] min-h-[600px] sm:h-screen sm:min-h-[760px] w-full overflow-hidden" data-testid="hero-section">
                <img src={HERO_IMG} alt="Wild Coffee Retreat treehouse cabin" className="absolute inset-0 w-full h-full object-cover animate-slow-zoom" />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative h-full max-w-[1500px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-16 sm:pb-24">
                    <div className="max-w-3xl text-bone fade-up">
                        <div className="eyebrow text-bone/80 mb-6" style={{ color: "rgba(250,248,245,0.8)" }}>{t("common.adultsOnly")}</div>
                        <h1 className="font-chiller text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight font-light">
                            <span style={{ color: "#1FA84F" }}>{t("hero.titleGreen")}</span>{" "}
                            <span style={{ color: "#F6C544" }}>{t("hero.titleGold")}</span><br />
                            <span style={{ color: "#E53935" }}>{t("hero.titleRed")}</span>
                        </h1>
                        <p className="mt-8 max-w-xl text-base lg:text-lg font-light text-bone/90 leading-relaxed">
                            {t("hero.sub")}
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4 items-center">
                            <Link to="/book" data-testid="hero-book-btn" className="inline-flex items-center gap-3 px-8 py-4 bg-terracotta text-bone hover:bg-terracotta/90 transition-colors text-xs uppercase tracking-widest2 font-semibold">
                                {t("hero.ctaBook")} <ArrowUpRight size={16} />
                            </Link>
                            <Link to="/cabins" data-testid="hero-explore-btn" className="inline-flex items-center gap-3 px-8 py-4 border border-bone/40 text-bone hover:bg-bone/10 transition-colors text-xs uppercase tracking-widest2 font-semibold">
                                {t("hero.ctaExplore")}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* INTRO STRIPE */}
            <section className="py-24 lg:py-32 px-6 lg:px-12 bg-bone">
                <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5">
                        <div className="eyebrow mb-6">No. 01 — Welcome</div>
                        <h2 className="font-chiller text-4xl lg:text-6xl leading-[1.05] tracking-tight text-jade">
                            <span style={{ color: "#1FA84F" }}>Everyone is</span> <span style={{ color: "#F6C544" }}>going to</span> <span style={{ color: "#E53935" }}>love this</span><br />
                        </h2>
                    </div>
                    <div className="md:col-span-6 md:col-start-7 space-y-6 text-base text-muted2 leading-relaxed">
                        <p>
                            Secludedly tucked away “Kotch pon a Rock in Negril, Jamaica."
                            Seven staggeringly bold, art-inspired cabins, embraced by lush gardens, where birds sing, 
                            Hummingbirds feed and dart through the trees, defending their turf.
                        </p>
                        <p>
                            Our carefully designed cabins feature air conditioning, ceiling fans, 
                            private bathrooms with hot and cold water, refrigerators, inviting verandas, 
                            and kitchenettes, with high-speed internet available throughout the property.
                        </p>
                        <p>
                            A tucked-away plunge pool with a floating tray for drinks and snacks, 
                            comfortable seating, and swing area—an onsite Jubie's Farm eatery and   (Di Fukin Bar.
                        </p>

                        <div className="flex gap-10 pt-6">
                            <Stat n="07" label="Art cabins" />
                            <Stat n="18" label="Max guests" />
                            <Stat n="3×" label="Traveler's Choice" accent="gold" />
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURED CABINS */}
            <section className="py-24 lg:py-32 px-6 lg:px-12 bg-bone2/40">
                <div className="max-w-[1500px] mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                        <div>
                            <div className="eyebrow mb-4">No. 02 — The Cabins</div>
                            <h2 className="font-chiller text-4xl lg:text-6xl leading-tight max-w-2xl">
                                <span style={{ color: "#1FA84F" }}>Art-cabin treehouses</span> <span style={{ color: "#F6C544" }}>each a</span> <span style={{ color: "#E53935" }}>one-of-one.</span>
                            </h2>
                        </div>
                        <Link to="/cabins" data-testid="home-view-all-cabins" className="text-sm uppercase tracking-widest2 font-semibold text-forest hover:text-terracotta inline-flex items-center gap-2">
                            View all cabins <ArrowUpRight size={16} />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        {cabins.slice(0, 3).map((c, i) => (
                            <Link key={c.id} to={`/cabins/${c.slug}`} className="card-hover block group" data-testid={`featured-cabin-${i}`}>
                                <div className="aspect-[3/4] overflow-hidden bg-bone2 mb-5">
                                    <img src={imgSrc(c.images?.[0])} alt={c.name} className="card-img w-full h-full object-cover" />
                                </div>
                                <div className="flex items-center justify-between text-xs uppercase tracking-widest2 text-espresso mb-2">
                                    <span>Cabin No. {String(i + 1).padStart(2, "0")}</span>
                                    <span style={{ color: "#E53935" }}>{format(c.price_per_night)}/nt</span>
                                </div>
                                <h3 className="font-chiller text-3xl text-ink leading-tight mb-2" style={{ color: "#1FA84F" }}>{c.name}</h3>
                                <p className="text-sm text-muted2 leading-relaxed line-clamp-2">{c.tagline}</p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* EDITORIAL SPLIT */}
            <section className="py-24 lg:py-32 px-6 lg:px-12 bg-bone">
                <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 relative">
                        <img src={LANDSCAPE_IMG} alt="Zinc Fence Rest cabin entrance" className="w-full aspect-[5/4] object-cover" style={{ objectPosition: "center 38%" }} />
                        <img src={INTERIOR_IMG} alt="Art cabin bedroom interior" className="hidden md:block absolute -bottom-12 -right-12 w-1/2 aspect-[3/4] object-cover shadow-2xl border-8 border-bone" />
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9">
                        <div className="eyebrow mb-6">No. 03 — The Place</div>
                        <h2 className="font-chiller text-4xl lg:text-5xl leading-tight tracking-tight mb-6">
                            A hidden gem. <em className="not-italic text-terracotta">Home of Artical people.</em>
                        </h2>
                        <p className="text-base text-muted2 leading-relaxed mb-4">
                            Voted a Traveler's Choice winner three consecutive years, Wild Coffee Retreat is a peaceful escape that everyone is gonna love — for singles, couples, or medium-sized groups of up to eighteen adults.
                        </p>
                        <Link to="/story" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 font-semibold text-forest hover:text-terracotta">
                            Read the full story <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* EXPERIENCES */}
            <section className="py-24 lg:py-32 px-6 lg:px-12 bg-forestDark text-bone">
                <div className="max-w-[1500px] mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                        <div>
                            <div className="eyebrow text-bone/60 mb-4">No. 04 — Experiences</div>
                            <h2 className="font-chiller text-4xl lg:text-6xl leading-tight max-w-2xl">Rituals to fold into your days.</h2>
                        </div>
                        <Link to="/experiences" className="text-sm uppercase tracking-widest2 font-semibold text-terracotta hover:text-bone inline-flex items-center gap-2" data-testid="home-view-experiences">
                            All experiences <ArrowUpRight size={16} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {experiences.map((e, i) => (
                            <div key={e.id} className="group" data-testid={`featured-experience-${i}`}>
                                <div className="aspect-[4/5] overflow-hidden bg-bone/10 mb-5">
                                    <img src={imgSrc(e.image)} alt={e.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                                </div>
                                <div className="flex items-center gap-2 text-xs uppercase tracking-widest2 text-bone/60 mb-2">
                                    <span>{e.category}</span> · <span>{e.duration}</span>
                                </div>
                                <h3 className="font-chiller text-3xl mb-2">{e.name}</h3>
                                <p className="text-sm text-bone/70 leading-relaxed">{e.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* VALUES */}
            <section className="py-24 lg:py-32 px-6 lg:px-12 bg-bone">
                <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20">
                    <Value icon={<Leaf />} title="Lush gardens, full Wi-Fi" text="Treehouse cabins hugged by tropical gardens, served by high-speed internet across the whole property — slow living, fully connected." />
                    <Value icon={<Coffee />} title="On-site eatery & bar" text="Warm hospitality meets bold artistic design at the Wild Coffee bar and kitchen — open to the public from morning espresso to evening cocktail." />
                    <Value icon={<Sparkles />} title="Bold artistic design" text="Each cabin makes you pause and wonder what the artist was thinking. Plunge pool with swinging seats. Hummingbirds on the house." />
                </div>
            </section>

            {/* FEATURED MOMENTS (admin-curated gallery) */}
            {featured.length > 0 && (
                <section className="py-24 lg:py-32 px-6 lg:px-12 bg-bone2/40" data-testid="home-moments">
                    <div className="max-w-[1500px] mx-auto">
                        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                            <div>
                                <div className="eyebrow mb-3 text-forest">{t("home.momentsEyebrow")}</div>
                                <h2 className="font-chiller text-4xl lg:text-5xl leading-tight">{t("home.momentsTitle")}</h2>
                            </div>
                            <Link to="/gallery" data-testid="home-moments-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 text-forest hover:text-terracotta transition-colors">
                                {t("home.momentsCta")} <ArrowUpRight size={15} />
                            </Link>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                            {featured.slice(0, 8).map((img, i) => (
                                <Link
                                    key={img.id}
                                    to="/gallery"
                                    data-testid={`home-moment-${i}`}
                                    className={`group relative overflow-hidden bg-bone2 ${i % 5 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"}`}
                                >
                                    <img src={imgSrc(img.url)} alt={img.caption || `Moment ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    {img.caption && (
                                        <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-espresso/80 to-transparent text-bone px-3 py-2 text-xs font-serif opacity-0 group-hover:opacity-100 transition-opacity">{img.caption}</span>
                                    )}
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="py-32 px-6 lg:px-12 bg-terracotta/95 text-bone">
                <div className="max-w-[1100px] mx-auto text-center">
                    <div className="eyebrow text-bone/80 mb-6">Reservations open year-round</div>
                    <h2 className="font-chiller text-5xl lg:text-6xl leading-tight mb-8">Stay with us.</h2>
                    <p className="max-w-xl mx-auto text-base text-bone/90 leading-relaxed mb-10">
                        Our seven art cabins book quickly — especially around Traveler's Choice season. Tell us when you'd like to come and we'll hold the right perch for your party of up to eighteen.
                    </p>
                    <Link to="/book" data-testid="home-cta-book" className="inline-flex items-center gap-3 px-10 py-4 bg-bone text-forest hover:bg-bone/90 transition-colors text-xs uppercase tracking-widest2 font-semibold">
                        Begin your reservation <ArrowUpRight size={16} />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Stat({ n, label, accent }) {
    const color = accent === "gold" ? "text-gold" : accent === "red" ? "text-terracotta" : "text-forest";
    return (
        <div>
            <div className={`font-serif text-4xl ${color}`}>{n}</div>
            <div className="eyebrow mt-1">{label}</div>
        </div>
    );
}

function Value({ icon, title, text }) {
    return (
        <div>
            <div className="text-forest mb-6">{icon}</div>
            <h3 className="font-chiller text-2xl mb-3 text-ink">{title}</h3>
            <p className="text-sm text-muted2 leading-relaxed">{text}</p>
        </div>
    );
}
